import { EllipsisVerticalIcon, EnvelopeIcon, TrashIcon, UserIcon } from '@heroicons/react/24/solid';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import DeleteRepresentativeDialog from '@/app/components/reps/delete-representative-dialog.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { useAuth } from '@/app/hooks/auth-provider.tsx';
import {
  ResponsiveTable,
  TableTile,
  TableTileField,
  TableTileList,
} from '@/primitives/responsive-table.tsx';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { TextLink } from '@/primitives/text.tsx';
import type {
  RepresentativeListModel,
  RepresentativeSearchModel,
} from '../../types/representative.type';
import { AvatarDisplay } from '../avatar-display';
import { EditEmailDialog } from '../edit-email-dialog.tsx';
import { NoData } from '../no-data';
import { Paginator, usePagination } from '../paginator';

function AdminRepName({ rep }: { rep: RepresentativeListModel }) {
  const displayName = `${rep.title ? `${rep.title} ` : ''}${rep.firstName} ${rep.lastName}`;

  return (
    <div className='flex flex-row items-center gap-2'>
      <AvatarDisplay
        firstName={rep.firstName}
        lastName={rep.lastName}
        imageUrl={rep.profilePhotoUrl}
        className='size-6'
      />
      <TextLink title='View profile' href={`/rep-public-profile/${rep.key}`}>
        {displayName}
      </TextLink>
    </div>
  );
}

function AdminRepActions({
  value,
  onImpersonate,
  onEditEmail,
  onDelete,
}: {
  value: RepresentativeSearchModel;
  onImpersonate: () => void;
  onEditEmail: () => void;
  onDelete: () => void;
}) {
  const canDelete = Boolean(value.representative.canDelete);

  return (
    <Dropdown placement='bottom-end'>
      <DropdownTrigger>
        <Button isIconOnly size='sm' variant='flat' color='default'>
          <EllipsisVerticalIcon className='size-5' />
        </Button>
      </DropdownTrigger>
      <DropdownMenu>
        <DropdownItem
          startContent={<UserIcon className='size-5' />}
          key='edit'
          onPress={onImpersonate}
        >
          Impersonate
        </DropdownItem>
        <DropdownItem
          key='approve'
          onPress={onEditEmail}
          startContent={<EnvelopeIcon className='size-5' />}
        >
          Edit email
        </DropdownItem>
        <DropdownItem
          key='delete'
          onPress={onDelete}
          startContent={<TrashIcon className='size-5' />}
          className={canDelete ? 'text-danger' : 'text-default-500'}
        >
          Delete Representative
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}

const AdminRepTable = ({ data }: { data: RepresentativeSearchModel[] }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RepresentativeListModel>(null);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [editEmailUser, setEditEmailUser] = useState<RepresentativeListModel>(null);
  const memoizedSortingDataAccessor = useCallback(
    (value: RepresentativeSearchModel, col: string) => {
      switch (col) {
        case 'createdAt':
        case 'lastLoggedInAt': {
          const date = DateTime.fromISO(_.get(value, col));
          return date.isValid ? date : '';
        }
        default:
          return _.get(value, col);
      }
    },
    [],
  );
  const { sort, setSort, sortData } = useSort({
    data,
    memoizedSortingDataAccessor,
  });
  const paginator = usePagination({
    data: sortData,
    pageSizes: [10, 25, 45],
    resourceName: 'reps',
  });

  const { impersonateUser } = useAuth();

  const { pagedData } = paginator;

  const openDeleteDialog = (value: RepresentativeListModel) => {
    setDeleteTarget(value);
    setDeleteDialogOpen(true);
  };

  const openEditEmail = (value: RepresentativeListModel) => {
    setEditEmailUser(value);
    setEditEmailOpen(true);
  };

  const hasReps = Boolean(pagedData?.length);

  return (
    <>
      <ResponsiveTable
        currentSort={sort}
        setCurrentSort={setSort}
        tiles={
          <TableTileList empty={!hasReps} emptyContent={<NoData />}>
            {hasReps &&
              pagedData.map(value => (
                <TableTile key={value.representative.id}>
                  <div className='flex flex-col gap-3'>
                    <TableTileField label='Name'>
                      <AdminRepName rep={value.representative} />
                    </TableTileField>
                    <TableTileField label='Company'>{value.representative.company}</TableTileField>
                    <TableTileField label='Email'>
                      <TextLink href={`mailto:${value.representative.emailAddress}`}>
                        {value.representative.emailAddress}
                      </TextLink>
                    </TableTileField>
                    <TableTileField label='Created'>
                      {value.representative.createdAt
                        ? formatDate(value.representative.createdAt, DateTime.DATETIME_SHORT)
                        : null}
                    </TableTileField>
                    <TableTileField label='Last Logged In'>
                      {value.representative.lastLoggedInAt
                        ? formatDate(value.representative.lastLoggedInAt, DateTime.DATETIME_SHORT)
                        : null}
                    </TableTileField>
                    <TableTileField label='Subscription Status'>
                      {_.capitalize(value.representative.subscriptionStatus)}
                    </TableTileField>
                    <TableTileField label='Actions'>
                      <AdminRepActions
                        value={value}
                        onImpersonate={() => impersonateUser(value.representative.key)}
                        onEditEmail={() => openEditEmail(value.representative)}
                        onDelete={() => openDeleteDialog(value.representative)}
                      />
                    </TableTileField>
                  </div>
                </TableTile>
              ))}
          </TableTileList>
        }
      >
        <TableHead>
          <TableRow>
            <TableHeader sortColumn='representative.lastName'>Name</TableHeader>
            <TableHeader sortColumn='representative.company'>Company</TableHeader>
            <TableHeader sortColumn='representative.emailAddress'>Email</TableHeader>
            <TableHeader sortColumn='representative.createdAt'>Created</TableHeader>
            <TableHeader sortColumn='representative.lastLoggedInAt'>Last Logged In</TableHeader>
            <TableHeader sortColumn='representative.subscriptionStatus'>
              Subscription Status
            </TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {!hasReps && (
            <TableRow>
              <TableCell colSpan={7}>
                <NoData />
              </TableCell>
            </TableRow>
          )}
          {hasReps &&
            pagedData.map(value => (
              <TableRow key={value.representative.id}>
                <TableCell>
                  <AdminRepName rep={value.representative} />
                </TableCell>
                <TableCell>{value.representative.company}</TableCell>
                <TableCell>
                  <TextLink href={`mailto:${value.representative.emailAddress}`}>
                    {value.representative.emailAddress}
                  </TextLink>
                </TableCell>
                <TableCell>
                  {value.representative.createdAt
                    ? formatDate(value.representative.createdAt, DateTime.DATETIME_SHORT)
                    : null}
                </TableCell>
                <TableCell>
                  {value.representative.lastLoggedInAt
                    ? formatDate(value.representative.lastLoggedInAt, DateTime.DATETIME_SHORT)
                    : null}
                </TableCell>
                <TableCell>{_.capitalize(value.representative.subscriptionStatus)}</TableCell>
                <TableCell>
                  <AdminRepActions
                    value={value}
                    onImpersonate={() => impersonateUser(value.representative.key)}
                    onEditEmail={() => openEditEmail(value.representative)}
                    onDelete={() => openDeleteDialog(value.representative)}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </ResponsiveTable>
      <Paginator {...paginator} />

      <DeleteRepresentativeDialog
        representative={deleteTarget}
        isOpen={deleteDialogOpen}
        setIsOpen={setDeleteDialogOpen}
      />

      <EditEmailDialog isOpen={editEmailOpen} setIsOpen={setEditEmailOpen} user={editEmailUser} />
    </>
  );
};

export default AdminRepTable;
